import {zodResolver} from "@hookform/resolvers/zod";
import {ArrowLeft, CheckCircle, Loader2} from "lucide-react";
import {useState} from "react";
import {useForm} from "react-hook-form";
import {Link} from "react-router-dom";
import {z} from "zod";

import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";
import {PublicPageShell} from "@/src/modules/core/components";
import {LOGO_URL_DESKTOP} from "@/src/modules/core/utils/constants";

import {useForgot} from "../hooks";
import {forgotPasswordSchema} from "../schemas";

export default function ForgotPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {mutateAsync: forgot, isPending} = useForgot();
  const form = useForm<z.infer<typeof forgotPasswordSchema>>({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(forgotPasswordSchema),
  });

  const handleSubmit = (data: z.infer<typeof forgotPasswordSchema>) => {
    try {
      setIsSubmitted(true);

      forgot(data);
    } catch (error) {
      console.log(error);
    }
  };

  const {
    watch,
    formState: {isSubmitting},
  } = form;

  const email = watch("email");

  return (
    <PublicPageShell>
      <div className="rounded-lg bg-background p-6 shadow-lg sm:p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center justify-center h-16 w-16 rounded-full">
              <img src={LOGO_URL_DESKTOP} alt="Image" height="100" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">STARSPRO</h1>
            <p className="text-muted-foreground">Security Management System</p>
          </div>

          {!isSubmitted ? (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-2 text-center">
                Reset your password
              </h2>
              <p className="text-muted-foreground text-center mb-6">
                Enter your email address and we'll send you instructions to reset your password.
              </p>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({field, formState}) => (
                      <FormItem>
                        <FormLabel>Email address</FormLabel>
                        <FormControl>
                          <Input
                            id="email"
                            type="email"
                            placeholder="name@company.com"
                            className="border-border"
                            disabled={isPending}
                            {...field}
                          />
                        </FormControl>
                        {formState.errors.email && (
                          <FormError error={formState.errors.email.message} />
                        )}
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className={cn(
                      "w-full bg-primary hover:bg-primary/90",
                      isPending && "opacity-80 cursor-not-allowed",
                    )}
                    disabled={isPending || isSubmitting}
                  >
                    {isPending || isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 size-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send reset instructions"
                    )}
                  </Button>
                </form>
              </Form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-16 w-16 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Check your email</h2>
              <p className="text-muted-foreground mb-6">
                We've sent password reset instructions to{" "}
                <span className="font-medium">{email}</span>
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <Button
                variant="outline"
                className="border-border"
                onClick={() => {
                  setIsSubmitted(false);
                }}
              >
                Try again
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/90"
            >
              <ArrowLeft className="mr-2 size-4" />
              Back to login
            </Link>
          </div>
        </div>

      <div className="mt-6 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} STARSPRO. All rights reserved.
      </div>
    </PublicPageShell>
  );
}
